// ─────────────────────────────────────────────────────────────────────────────
// contentSectionEditPageCareer.tsx — Page
// ─────────────────────────────────────────────────────────────────────────────
import { saveUpdateContent, CONTENT_STORE_KEYS } from '../../../../../../context-controller-mod/context-serv/global-provider/local-storage-context/updateContentStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../css/contentSectionEditPageCareer.css';
import Cookies from 'js-cookie';
import ApiGet    from '../../../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPut    from '../../../../../../api-controller-mod/api-serv/put-api/putApi';
import ApiDelete from '../../../../../../api-controller-mod/api-serv/delete-api/deleteApi';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import TextField from '../../../../../../component-controller-mod/component-serv/input-fs-cp/text-field-op/ts/textField';
import { Button, Confirmation, Toggle } from '@core/uilibrary';
import { useGlobalPopupProvider } from '../../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { DocConfirmationIcon, SuccessDocConfirmationIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import EditContentBreadcrumb from '../../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/edit-content-breadcrumb/ts/editContentBreadcrumb';

import {
  HeroSection, PositionSection, SubmissionStepSection,
  SlugResponse, UpdateEntry, ActiveSection,
} from './types';
import { SECTION_HUMAN, SECTION_KEY } from './constants';
import HeroEditor           from './heroEditor';
import PositionEditor       from './positionEditor';
import SubmissionStepEditor from './submissionStepEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const ContentSectionEditPageCareer: React.FC = () => {
  const { sectionId, contentType, contentUuid: paramContentUuid, contentVersionUuid: paramContentVersionUuid } = useParams<{ contentType: string; sectionId: string; contentUuid: string; contentVersionUuid: string }>();
  const navigate      = useNavigate();
  const { showToast } = useToast();
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();

  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [contentUuid,    setContentUuid]    = useState(paramContentUuid ?? '');
  const [metaStatus,     setMetaStatus]     = useState('PUBLISHED');

  const [hero,           setHero]           = useState<HeroSection           | null>(null);
  const [position,       setPosition]       = useState<PositionSection       | null>(null);
  const [submissionStep, setSubmissionStep] = useState<SubmissionStepSection | null>(null);

  const [heroVisible,           setHeroVisible]           = useState(true);
  const [positionVisible,       setPositionVisible]       = useState(true);
  const [submissionStepVisible, setSubmissionStepVisible] = useState(true);

  const userId             = Cookies.get('userUuid')  ?? '';
  const username           = Cookies.get('username')  ?? '';
  const contentVersionUuid = paramContentVersionUuid ?? Cookies.get('contentVersionUuid') ?? '';

  const isSavedRef = useRef(false);
  const origTitle  = useRef<string>('');
  const origStatus = useRef<string>('PUBLISHED');
  const origAuthor = useRef<string>('');

  const origHero           = useRef<HeroSection           | null>(null);
  const origPosition       = useRef<PositionSection       | null>(null);
  const origSubmissionStep = useRef<SubmissionStepSection | null>(null);

  const activeSectionKey: ActiveSection = (() => {
    if (sectionId === 'positionSection')       return 'positionSection';
    if (sectionId === 'submissionStepSection') return 'submissionStepSection';
    return 'heroSection';
  })();

  const sectionLabel = SECTION_HUMAN[activeSectionKey];

  // ── Fetch by slug ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    ApiGet<SlugResponse>(
      `${API_ENDPOINTS.GET_CMS_CONTENT}?slug=career`
    )
      .then(res => {
        if (cancelled) return;
        const resData = res?.data?.data;
        const cta     = resData?.data?.career?.cta?.[0];
        if (!cta) return;

        // Never overwrite a UUID that came from the URL param
        setContentUuid(prev => prev || String(resData.contentId ?? ''));

        setHero(cta.heroSection);
        setPosition(cta.positionSection);
        setSubmissionStep(cta.submissionStepSection);

        setHeroVisible(cta.heroSection?.visible ?? true);
        setPositionVisible(cta.positionSection?.visible ?? true);
        setSubmissionStepVisible(cta.submissionStepSection?.visible ?? true);

        origHero.current           = cta.heroSection;
        origPosition.current       = cta.positionSection;
        origSubmissionStep.current = cta.submissionStepSection;

        const active =
          activeSectionKey === 'positionSection'       ? cta.positionSection :
          activeSectionKey === 'submissionStepSection' ? cta.submissionStepSection :
          cta.heroSection;

        origTitle.current  = (active as any)?.headline
          ?? (active as any)?.content?.[0]?.headerSection?.title
          ?? '';
        origAuthor.current = username;
        setMetaStatus((active as any)?.status ?? 'PUBLISHED');
        origStatus.current = (active as any)?.status ?? 'PUBLISHED';
      })
      .catch(err => { if (!cancelled) console.error('[CareerSectionEdit] fetch:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeSectionKey]);

  // ── Delete draft ───────────────────────────────────────────────────────────
  /**
   * isDelete logic (mirrors product / sale / business / jobDetail / navigation / contact):
   *  - key missing        → call delete API + remove cookie
   *  - isDelete === false → skip delete API, remove only isDelete key
   *  - isDelete === true  → call delete API + remove cookie
   */
  const deleteDraft = useCallback(async () => {
    if (!contentVersionUuid) return;

    const raw      = localStorage.getItem('isDelete');
    const isDelete = raw !== null
      ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
      : null;

    if (isDelete === false) {
      localStorage.removeItem('isDelete');
      return;
    }

    localStorage.removeItem('isDelete');
    try {
      await ApiDelete(
        `${API_ENDPOINTS.DELETE_DRAFT_CONTENT}?version=${contentVersionUuid}`
      );
    } catch (err) {
      console.warn('[CareerSectionEdit] delete draft failed:', err);
    } finally {
      Cookies.remove('contentVersionUuid');
    }
  }, [contentVersionUuid]);

  // ── On unmount ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!isSavedRef.current) {
        deleteDraft();
      } else {
        localStorage.removeItem('isDelete');
      }
    };
  }, [deleteDraft]);

const buildUpdates = useCallback((nextStatus: string): UpdateEntry[] => {
  const updates:      UpdateEntry[] = [];
  const fieldUpdates: UpdateEntry[] = [];

  // ── Hero ──────────────────────────────────────────────────────────────────
  if (activeSectionKey === 'heroSection' && hero && origHero.current) {
    const orig = origHero.current;
    const BASE = ['career', 'cta', 0, 'heroSection'] as const;

    // visible — always send
    fieldUpdates.push({ path: [...BASE, 'visible'],                   value: heroVisible });
    // primaryButton.visible — always send
    fieldUpdates.push({ path: [...BASE, 'primaryButton', 'visible'], value: hero.primaryButton.visible });

    if (hero.headline !== orig.headline)
      fieldUpdates.push({ path: [...BASE, 'headline'],                value: hero.headline });
    if (hero.primaryButton.label !== orig.primaryButton.label)
      fieldUpdates.push({ path: [...BASE, 'primaryButton', 'label'], value: hero.primaryButton.label });

    updates.push(...fieldUpdates);
    updates.push({ path: [...BASE, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Position ──────────────────────────────────────────────────────────────
  if (activeSectionKey === 'positionSection' && position && origPosition.current) {
    const BASE   = ['career', 'cta', 0, 'positionSection']                                 as const;
    const HEADER = ['career', 'cta', 0, 'positionSection', 'content', 0, 'headerSection'] as const;
    const orig   = origPosition.current;

    // section visible — always send
    fieldUpdates.push({ path: [...BASE, 'visible'], value: positionVisible });

    const currH = position.content[0]?.headerSection;
    const origH = orig.content[0]?.headerSection;
    if (currH && origH) {
      // header visible — always send
      fieldUpdates.push({ path: [...HEADER, 'visible'],                 value: currH.visible });
      // actionButton.visible — always send
      fieldUpdates.push({ path: [...HEADER, 'actionButton', 'visible'], value: currH.actionButton.visible });

      if (currH.label !== origH.label)
        fieldUpdates.push({ path: [...HEADER, 'label'],                 value: currH.label });
      if (currH.title !== origH.title)
        fieldUpdates.push({ path: [...HEADER, 'title'],                 value: currH.title });
      if (currH.actionButton.label !== origH.actionButton.label)
        fieldUpdates.push({ path: [...HEADER, 'actionButton', 'label'], value: currH.actionButton.label });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...BASE, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
    fieldUpdates.length = 0;
  }

  // ── Submission Step ────────────────────────────────────────────────────────
  if (activeSectionKey === 'submissionStepSection' && submissionStep && origSubmissionStep.current) {
    const BASE   = ['career', 'cta', 0, 'submissionStepSection']                                 as const;
    const HEADER = ['career', 'cta', 0, 'submissionStepSection', 'content', 0, 'headerSection'] as const;
    const orig   = origSubmissionStep.current;

    // section visible — always send
    fieldUpdates.push({ path: [...BASE, 'visible'], value: submissionStepVisible });

    const currH = submissionStep.content[0]?.headerSection;
    const origH = orig.content[0]?.headerSection;
    if (currH && origH) {
      // header visible — always send
      fieldUpdates.push({ path: [...HEADER, 'visible'],         value: currH.visible });
      // list.visible — always send
      fieldUpdates.push({ path: [...HEADER, 'list', 'visible'], value: currH.list.visible });

      if (currH.label !== origH.label)
        fieldUpdates.push({ path: [...HEADER, 'label'],         value: currH.label });
      if (currH.title !== origH.title)
        fieldUpdates.push({ path: [...HEADER, 'title'],         value: currH.title });
      if (currH.description !== origH.description)
        fieldUpdates.push({ path: [...HEADER, 'description'],   value: currH.description });

      const itemsChanged = currH.list.items.some((item, idx) => {
        const origItem = origH.list.items[idx];
        if (!origItem) return true;
        return (
          item.title       !== origItem.title       ||
          item.description !== origItem.description ||
          (item.button?.title ?? '') !== (origItem.button?.title ?? '')
        );
      });
      if (itemsChanged)
        fieldUpdates.push({ path: [...HEADER, 'list', 'items'], value: currH.list.items });
    }

    updates.push(...fieldUpdates);
    updates.push({ path: [...BASE, 'status'], value: nextStatus !== origStatus.current ? nextStatus : 'DRAFT' });
  }

  return updates;
}, [
  activeSectionKey,
  hero, position, submissionStep,
  heroVisible, positionVisible, submissionStepVisible,
]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const performSave = useCallback(async () => {
    setSaving(true);
    try {
      await ApiPut(
        `${API_ENDPOINTS.UPDATE_CONTENT_BY_PATH}?contentUuid=${contentUuid}&userId=${userId}&versionUuid=${contentVersionUuid}`,
        { type: 'page', title: 'Career Content', version: 1, updates: buildUpdates(metaStatus) },
      );
      isSavedRef.current = true;
      localStorage.removeItem('isDelete');
      saveUpdateContent(
        CONTENT_STORE_KEYS.CAREER,
        contentType ?? 'career', contentUuid, contentVersionUuid, activeSectionKey,
      );
      Cookies.remove('contentVersionUuid');
      showToast({ status: 'success', title: 'Complete !',   description: 'Section was updated successfully.' });
      navigate(`/workflow/manage/preview/${contentUuid}/${contentVersionUuid}/draft`);
    } catch (err) {
      console.error('[CareerSectionEdit] save:', err);
      showToast({ status: 'error',   title: 'Uncomplete !', description: 'Section was updated unsuccessfully.' });
    } finally {
      setSaving(false);
    }
  }, [buildUpdates, metaStatus, contentUuid, userId, contentVersionUuid, contentType, activeSectionKey, navigate, showToast]);

  // ── Confirm save ───────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    openConfirm(
      <Confirmation
        visible
        title="Save changes"
        description={`Are you sure you want to save changes to the "${sectionLabel}" section? This will update the live content.`}
        status="info"
        icon={<SuccessDocConfirmationIcon />}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost"   size="md" value="Cancel" type="button" onClick={closeConfirm} />
            <Button variant="primary" size="md" value="Save"   type="button"
              onClick={() => { closeConfirm(); performSave(); }}
            />
          </>
        }
      />
    );
  }, [openConfirm, closeConfirm, sectionLabel, performSave]);

  // ── Confirm cancel / discard ───────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    openConfirm(
      <Confirmation
        visible
        title="Discard changes"
        description="Are you sure you want to discard all unsaved changes? This action cannot be undone."
        status="warning"
        icon={<DocConfirmationIcon />}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost"  size="md" value="Cancel"    type="button" onClick={closeConfirm} />
            <Button variant="danger" size="md" value="Discard" type="button"
              onClick={async () => {
                closeConfirm();
                await deleteDraft();
                isSavedRef.current = true;
                localStorage.removeItem('isDelete');
                navigate(`/content/${contentType ?? 'career'}`);
              }}
            />
          </>
        }
      />
    );
  }, [openConfirm, closeConfirm, navigate, contentType, deleteDraft]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="scl--cms-content-edit-career-root">
        <div className="scl--cms-content-edit-career-skeleton-wrap">
          <div className="scl--cms-content-edit-career-skeleton scl--cms-content-edit-career-skeleton--title" />
          <div className="scl--cms-content-edit-career-skeleton scl--cms-content-edit-career-skeleton--desc"  />
          <div className="scl--cms-content-edit-career-skeleton scl--cms-content-edit-career-skeleton--field" />
          <div className="scl--cms-content-edit-career-skeleton scl--cms-content-edit-career-skeleton--field" />
          <div className="scl--cms-content-edit-career-skeleton scl--cms-content-edit-career-skeleton--field" />
        </div>
      </div>
    );
  }

  if (!hero && !position && !submissionStep) {
    return (
      <div className="scl--cms-content-edit-career-root">
        <div className="scl--cms-content-edit-career-empty-state">
          <p className="scl--cms-content-edit-career-empty-title">Section not found</p>
          <p className="scl--cms-content-edit-career-empty-desc">
            Could not load "{activeSectionKey}" for career.
          </p>
          <Button variant="ghost" value="← Back" onClick={() => navigate(`/content/${contentType ?? 'career'}`)} />
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-content-edit-career-root">
      <EditContentBreadcrumb
        onHomeClick={() => { localStorage.removeItem('isDelete'); navigate('/overview'); }}
        onContentClick={() => { localStorage.removeItem('isDelete'); navigate(`/content/${contentType ?? 'career'}`); }}
      />

      <h1 className="scl--cms-content-edit-career-page-title">Edit section</h1>
      <p className="scl--cms-content-edit-career-page-desc">
        Responsible for managing and maintaining website content to ensure accuracy.
      </p>

      <div className="scl--cms-content-edit-career-meta-grid">
        <TextField label="Section ID" value={SECTION_KEY[activeSectionKey]} disabled />
        <TextField label="Author"     value={origAuthor.current} disabled />
        <TextField label="Section"    value={sectionLabel}       disabled />
        <TextField label="Title"      value={origTitle.current}  disabled />
      </div>

      <div className="scl--cms-content-edit-career-section-editor">
        <div className="scl--cms-content-edit-career-section-heading-row">
          <h2 className="scl--cms-content-edit-career-section-heading">
            Career-{sectionLabel} sector
          </h2>
          {activeSectionKey === 'heroSection'           && <Toggle checked={heroVisible}           onChange={setHeroVisible}           />}
          {activeSectionKey === 'positionSection'       && <Toggle checked={positionVisible}       onChange={setPositionVisible}       />}
          {activeSectionKey === 'submissionStepSection' && <Toggle checked={submissionStepVisible} onChange={setSubmissionStepVisible} />}
        </div>
        <div className="scl--cms-content-edit-career-divider" />

        {activeSectionKey === 'heroSection' && hero && (
          <HeroEditor data={hero} onChange={setHero} />
        )}
        {activeSectionKey === 'positionSection' && position && (
          <PositionEditor data={position} onChange={setPosition} />
        )}
        {activeSectionKey === 'submissionStepSection' && submissionStep && (
          <SubmissionStepEditor data={submissionStep} onChange={setSubmissionStep} />
        )}
      </div>

      <div className="scl--cms-content-edit-career-actions">
        <Button variant="ghost"   value="Cancel"                      onClick={handleCancel} />
        <Button variant="primary" value={saving ? 'Saving…' : 'Save'} onClick={handleSave}   disabled={saving} />
      </div>
    </div>
  );
};

export default ContentSectionEditPageCareer;